import React, {useEffect, useMemo, useRef} from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  fromPromise,
} from "@apollo/client";
import {setContext} from "@apollo/client/link/context";
import {onError} from "@apollo/client/link/error";
import {ApolloLink} from "@apollo/client";
import {isEmpty} from "underscore";
import PropTypes from "prop-types";

import {useLogin} from "../login/LoginProvider";
const GRAPHQL_API_URL = process.env.GRAPHQL_API_URL;

const ApolloClientProvider = ({children}) => {
  const {user, refreshSession} = useLogin();

  // Read auth from refs so the client is built once. Rebuilding it per render aborts
  // in-flight requests (NS_BINDING_ABORTED) and wipes the InMemoryCache.
  const userRef = useRef(user);
  const refreshSessionRef = useRef(refreshSession);
  useEffect(() => {
    userRef.current = user;
    refreshSessionRef.current = refreshSession;
  }, [user, refreshSession]);

  const client = useMemo(() => {
    const httpLink = createHttpLink({uri: GRAPHQL_API_URL});

    const authLink = setContext((_, {headers}) => ({
      headers: {
        ...headers,
        authorization: !isEmpty(userRef.current) ? `Bearer ${userRef.current.IdToken}` : "",
      },
    }));

    const errorLink = onError(({operation, forward, networkError}) => {
      if (networkError?.statusCode !== 401) return;
      // Retry a 401 at most once. If refreshSession can't produce a fresh token it
      // forces a re-login (returning none), so we stop instead of looping.
      if (operation.getContext().__authRetried) return;

      return fromPromise(refreshSessionRef.current().then((result) => result?.IdToken ?? null))
        .filter((token) => Boolean(token))
        .flatMap((token) => {
          const oldHeaders = operation.getContext().headers;
          operation.setContext({
            headers: {
              ...oldHeaders,
              authorization: `Bearer ${token}`,
            },
            __authRetried: true,
          });

          return forward(operation);
        });
    });

    return new ApolloClient({
      cache: new InMemoryCache(),
      link: ApolloLink.from([authLink, errorLink, httpLink]),
    });
  }, []);

  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
};

ApolloClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ApolloClientProvider;
